import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { colors } from "@/utils/colors";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { cn } from "@/lib/utils";
import { getSelectUnities } from "@/Api/unities";

type UnityOption = { id: number | string; name: string };

const isOffice = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .startsWith("ESCRIT");

/**
 * Estado do filtro de unidades das telas de RH. Abre já filtrado pela unidade
 * ESCRITÓRIO; lista vazia = todas as unidades (sem filtro). `param` é o valor
 * CSV pronto pro query string (`unity_id=1,2`), `undefined` quando sem filtro.
 */
export const useUnityFilter = ({
  withUnity = true,
  initialExtra,
}: { withUnity?: boolean; initialExtra?: Record<string, string> } = {}) => {
  const [unities, setUnities] = useState<UnityOption[]>([]);
  // `selected` é o rascunho do formulário; `applied` só muda em "Aplicar Filtros".
  const [selected, setSelected] = useState<string[]>([]);
  const [applied, setApplied] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [appliedName, setAppliedName] = useState("");
  // Filtros extras de cada tela (ex.: duração, documento), também com rascunho/aplicado.
  const [extra, setExtraState] = useState<Record<string, string>>(
    () => initialExtra ?? {}
  );
  const [appliedExtra, setAppliedExtra] = useState<Record<string, string>>(
    () => initialExtra ?? {}
  );
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Telas sem escopo por unidade (ex.: feriados gerais) não carregam as unidades.
    if (!withUnity) {
      setReady(true);
      return;
    }
    (async () => {
      const { data } = await getSelectUnities();
      const list: UnityOption[] = data?.response || [];
      setUnities(list);
      const office = list.find((unity) => isOffice(unity.name));
      const initial = office ? [String(office.id)] : [];
      setSelected(initial);
      setApplied(initial);
      setReady(true);
    })();
  }, []);

  return {
    unities,
    selected,
    setSelected,
    ready,
    applied,
    name,
    setName,
    appliedName,
    extra,
    setExtra: (key: string, value: string) =>
      setExtraState((prev) => ({ ...prev, [key]: value })),
    appliedExtra,
    apply: () => {
      setApplied(selected);
      setAppliedName(name.trim());
      setAppliedExtra(extra);
    },
    clear: () => {
      setSelected([]);
      setApplied([]);
      setName("");
      setAppliedName("");
      setExtraState({});
      setAppliedExtra({});
    },
    nameParam: appliedName || undefined,
    param: applied.length ? applied.join(",") : undefined,
  };
};

export type UnityFilterState = ReturnType<typeof useUnityFilter>;

export const UnityFilter = ({
  filter,
  className,
  namePlaceholder,
  hideUnity = false,
  children,
}: {
  filter: UnityFilterState;
  className?: string;
  /** Quando informado, exibe o campo de busca por nome dentro do card. */
  namePlaceholder?: string;
  /** Esconde o campo de unidade (telas gerais, sem escopo por unidade). */
  hideUnity?: boolean;
  /** Campos extras da tela (ex.: setor), renderizados após a unidade. */
  children?: ReactNode;
}) => {
  const {
    unities,
    selected,
    setSelected,
    applied,
    apply,
    clear,
    name,
    setName,
    appliedName,
    extra,
    appliedExtra,
  } = filter;
  const [isExpanded, setIsExpanded] = useState(false);

  const options = useMemo(
    () => unities.map((unity) => ({ value: String(unity.id), label: unity.name })),
    [unities]
  );

  const hasActiveFilters =
    selected.length > 0 ||
    applied.length > 0 ||
    !!name ||
    !!appliedName ||
    Object.values(extra).some(Boolean) ||
    Object.values(appliedExtra).some(Boolean);

  return (
    <Card className={cn("mb-1", className)}>
      <CardHeader className="p-3 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base sm:text-lg">Filtros</CardTitle>
          <div className="flex gap-2">
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clear} className="h-8">
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-8"
            >
              {isExpanded ? "Ocultar" : "Expandir"}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-3 pt-0 sm:p-4 sm:pt-0">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              apply();
            }}
            className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
          >
            {namePlaceholder && (
              <div className="space-y-1.5 w-full sm:w-60">
                <Label htmlFor="filter-name" className="text-sm">
                  Nome
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    id="filter-name"
                    placeholder={namePlaceholder}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-9 pl-9"
                  />
                </div>
              </div>
            )}
            {!hideUnity && (
            <div className="space-y-1.5 w-full sm:w-60">
              <Label className="text-sm">Unidade</Label>
              <MultiSelectCheckbox
                options={options}
                selectedValues={selected}
                onSelectionChange={setSelected}
                placeholder="Selecione a unidade"
                selectAllLabel="SELECIONAR TUDO"
                hideBadge
                showLabelsInTrigger
                className="space-y-1.5 [&_button[role=combobox]]:h-9"
              />
            </div>
            )}
            {children}
            <Button
              type="submit"
              className={cn("h-9 gap-2 w-full px-4 sm:ml-auto sm:w-auto", colors.bg_primary)}
            >
              <Search className="w-4 h-4" />
              Aplicar Filtros
            </Button>
          </form>
        </CardContent>
      )}
    </Card>
  );
};
